/**
 * Sync Service - Handles offline/online data synchronization
 * Syncs JSA signatures, incident reports, and training progress
 */

import { axiosInstance } from '../App';
import {
  getPendingSyncItems,
  markSyncItemComplete,
  markSyncItemFailed,
  getAllFromStore,
  saveToStore,
  saveBulkToStore,
  clearStore,
  addToSyncQueue
} from './offlineDB';

// Sync action types
export const SYNC_TYPES = {
  JSA_SIGNATURE: 'jsa_signature',
  INCIDENT_REPORT: 'incident_report',
  TRAINING_PROGRESS: 'training_progress',
  TRAINING_COMPLETE: 'training_complete',
  SAFETY_AUDIT: 'safety_audit'
};

// Check if online
export const isOnline = () => navigator.onLine;

// Queue a JSA signature for sync
export const queueJSASignature = async (jsaId, signatureData) => {
  await addToSyncQueue({
    type: SYNC_TYPES.JSA_SIGNATURE,
    jsaId,
    data: signatureData
  });
  console.log('📝 JSA signature queued for sync:', jsaId);
};

// Queue an incident report for sync
export const queueIncidentReport = async (incidentData) => {
  await addToSyncQueue({
    type: SYNC_TYPES.INCIDENT_REPORT,
    data: incidentData
  });
  console.log('🚨 Incident report queued for sync');
};

// Alias for queueIncidentReport (for creating new incidents)
export const queueIncidentCreate = queueIncidentReport;

// Queue training progress for sync
export const queueTrainingProgress = async (courseId, progressData) => {
  await addToSyncQueue({
    type: SYNC_TYPES.TRAINING_PROGRESS,
    courseId,
    data: progressData
  });
  console.log('📚 Training progress queued for sync:', courseId);
};

// Queue training completion for sync
export const queueTrainingComplete = async (courseId, completionData) => {
  await addToSyncQueue({
    type: SYNC_TYPES.TRAINING_COMPLETE,
    courseId,
    data: completionData
  });
  console.log('🎓 Training completion queued for sync:', courseId);
};

// Queue a safety audit for sync (offline creation)
export const queueSafetyAudit = async (auditData) => {
  await addToSyncQueue({
    type: SYNC_TYPES.SAFETY_AUDIT,
    data: auditData
  });
  console.log('🛡️ Safety audit queued for sync');
};

// Process a single sync item
const processSyncItem = async (item) => {
  try {
    switch (item.type) {
      case SYNC_TYPES.JSA_SIGNATURE:
        await axiosInstance.post(`/jsa/${item.jsaId}/acknowledge`, item.data);
        break;
        
      case SYNC_TYPES.INCIDENT_REPORT:
        await axiosInstance.post('/incident-reports', item.data);
        break;
        
      case SYNC_TYPES.TRAINING_PROGRESS:
        await axiosInstance.post(`/courses/${item.courseId}/progress`, item.data);
        break;
        
      case SYNC_TYPES.TRAINING_COMPLETE:
        await axiosInstance.post(`/courses/${item.courseId}/complete`, item.data);
        break;
        
      case SYNC_TYPES.SAFETY_AUDIT:
        await axiosInstance.post('/safety-audits', item.data);
        break;
        
      default:
        console.warn('Unknown sync type:', item.type);
        return false;
    }
    
    await markSyncItemComplete(item.id);
    console.log('✅ Synced:', item.type, item.id);
    return true;
  } catch (error) {
    console.error('❌ Sync failed:', item.type, error.message);
    await markSyncItemFailed(item.id, error.message);
    return false;
  }
};

// Sync all pending items
export const syncAll = async (onProgress) => {
  if (!isOnline()) {
    console.log('📴 Offline - cannot sync');
    return { success: 0, failed: 0, total: 0 };
  }
  
  const pendingItems = await getPendingSyncItems();
  const total = pendingItems.length;
  
  if (total === 0) {
    console.log('✨ Nothing to sync');
    return { success: 0, failed: 0, total: 0 };
  }
  
  console.log(`🔄 Syncing ${total} items...`);
  
  let success = 0;
  let failed = 0;
  
  for (let i = 0; i < pendingItems.length; i++) {
    const item = pendingItems[i];
    const result = await processSyncItem(item);
    
    if (result) {
      success++;
    } else {
      failed++;
    }
    
    if (onProgress) {
      onProgress({ current: i + 1, total, success, failed });
    }
  }
  
  console.log(`✅ Sync complete: ${success} success, ${failed} failed`);
  return { success, failed, total };
};

// Download data for offline use
export const downloadForOffline = async (userId, companyId, role) => {
  if (!isOnline()) {
    throw new Error('Cannot download while offline');
  }
  
  console.log('📥 Downloading data for offline use...');
  
  try {
    // Download JSAs
    const jsasResponse = await axiosInstance.get('/jsa');
    if (jsasResponse.data) {
      await saveBulkToStore('jsas', jsasResponse.data);
      console.log(`📋 Cached ${jsasResponse.data.length} JSAs`);
    }
    
    // Download Incidents (for supervisors)
    if (role === 'supervisor' || role === 'admin' || role === 'company_admin') {
      try {
        const incidentsResponse = await axiosInstance.get('/incident-reports');
        if (incidentsResponse.data) {
          await saveBulkToStore('incidents', incidentsResponse.data);
          console.log(`🚨 Cached ${incidentsResponse.data.length} incidents`);
        }
      } catch (e) {
        console.log('Could not cache incidents:', e.message);
      }
    }
    
    // Download Training Courses
    try {
      const coursesResponse = await axiosInstance.get('/courses');
      if (coursesResponse.data) {
        await saveBulkToStore('courses', coursesResponse.data);
        console.log(`📚 Cached ${coursesResponse.data.length} courses`);
      }
    } catch (e) {
      console.log('Could not cache courses:', e.message);
    }
    
    // Download Training Progress
    try {
      const progressResponse = await axiosInstance.get('/progress');
      if (progressResponse.data) {
        await saveBulkToStore('progress', progressResponse.data);
        console.log(`📊 Cached training progress`);
      }
    } catch (e) {
      console.log('Could not cache progress:', e.message);
    }
    
    // Download SDS documents list (not the actual files)
    try {
      const sdsResponse = await axiosInstance.get('/sds/library');
      if (sdsResponse.data) {
        await saveBulkToStore('sds', sdsResponse.data);
        console.log(`🧪 Cached ${sdsResponse.data.length} SDS documents`);
      }
    } catch (e) {
      console.log('Could not cache SDS:', e.message);
    }
    
    // Download Safety Audits (for supervisors and admins)
    if (role === 'supervisor' || role === 'admin' || role === 'company_admin') {
      try {
        const auditsResponse = await axiosInstance.get('/safety-audits');
        if (auditsResponse.data) {
          await saveBulkToStore('audits', auditsResponse.data);
          console.log(`🛡️ Cached ${auditsResponse.data.length} safety audits`);
        }
      } catch (e) {
        console.log('Could not cache audits:', e.message);
      }
    }
    
    // Save last sync time
    await saveToStore('userData', { 
      key: 'lastSync', 
      value: new Date().toISOString() 
    });
    
    console.log('✅ Offline data download complete');
    return true;
  } catch (error) {
    console.error('❌ Failed to download offline data:', error);
    throw error;
  }
};

// Get offline JSAs
export const getOfflineJSAs = async () => {
  return getAllFromStore('jsas');
};

// Get offline incidents
export const getOfflineIncidents = async () => {
  return getAllFromStore('incidents');
};

// Get offline courses
export const getOfflineCourses = async () => {
  return getAllFromStore('courses');
};

// Get offline training progress
export const getOfflineProgress = async () => {
  return getAllFromStore('progress');
};

// Get offline safety audits
export const getOfflineAudits = async () => {
  const audits = await getAllFromStore('audits');
  return audits || [];
};

// Get offline SDS documents
export const getOfflineSDS = async () => {
  return getAllFromStore('sds');
};

// Get pending sync count
export const getPendingSyncCount = async () => {
  const items = await getPendingSyncItems();
  return items.length;
};

// Setup auto-sync when coming online
export const setupAutoSync = (onSyncComplete) => {
  const handleOnline = async () => {
    console.log('🌐 Back online - starting auto-sync...');
    const result = await syncAll();
    if (onSyncComplete) {
      onSyncComplete(result);
    }
  };
  
  window.addEventListener('online', handleOnline);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
  };
};

export default {
  SYNC_TYPES,
  isOnline,
  queueJSASignature,
  queueIncidentReport,
  queueIncidentCreate,
  queueTrainingProgress,
  queueTrainingComplete,
  queueSafetyAudit,
  syncAll,
  downloadForOffline,
  getOfflineJSAs,
  getOfflineIncidents,
  getOfflineCourses,
  getOfflineProgress,
  getOfflineAudits,
  getOfflineSDS,
  getPendingSyncCount,
  setupAutoSync
};
